import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, SearchInput, Pagination, ActiveFilter, useDataTableUrl, useClientPage } from '@airclou/ui';
import type { DataTableColumn } from '@airclou/ui';
import { History, X } from 'lucide-react';
import { TABLE_CONFIG } from '@airclou/shared';
import { ErrorState } from '@/components/state/ErrorState';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeWithSeconds } from '@airclou/utils'

interface AuditLog {
  id: string;
  event_type: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  ip_address?: string;
  created_at: string;
}

// What the User column shows, and so what it sorts on.
const userLabel = (log: AuditLog) => log.user_email || log.user_name || log.user_id;

// Sortable column id → the value that column sorts on. A Map, not an object
// literal: the column id comes from the URL, and `?sortBy=valueOf` would find
// Object.prototype's methods on a plain object and crash the sort.
const SORT_VALUES = new Map<string, (log: AuditLog) => string | undefined>([
  ['event_type', (log) => log.event_type],
  ['user', userLabel],
  ['created_at', (log) => log.created_at],
]);

// Ascending order on one column. The list is sorted with this before it is cut
// into pages, and the table re-sorts each page with it too (as the column's
// `sortingFn`), so the two can never disagree about where a row belongs. A
// missing value sorts as the largest, matching what the table does with
// `undefined` before it ever calls a `sortingFn`.
function compareLogs(a: AuditLog, b: AuditLog, columnId: string): number {
  const value = SORT_VALUES.get(columnId);
  if (!value) return 0;
  const av = value(a);
  const bv = value(b);
  if (!av || !bv) return !av ? (!bv ? 0 : 1) : -1;
  const x = av.toLowerCase();
  const y = bv.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

export function AuditTab(props: {
  auditLogs: AuditLog[];
  hasError?: boolean;
  /** The first fetch is still running, so an empty table is not yet an answer. */
  isLoading?: boolean;
  onRetry?: () => void;
}) {
  const timezone = useTimezone()
  const { auditLogs, hasError, isLoading = false, onRetry } = props;
  const { t } = useTranslation('dashboard');

  // Sort, search and page live in the URL (same pattern as the org audit log),
  // so they survive a reload, a shared link and back/forward.
  const {
    search: searchQuery,
    page,
    limit: pageSize,
    sorting,
    onSortingChange: handleSortingChange,
    onSearchChange: handleSearchChange,
    onPageChange,
    clampPage,
  } = useDataTableUrl({ defaultLimit: TABLE_CONFIG.DEFAULT_PAGE_SIZE });

  // Filter audit logs based on search
  const filteredAuditLogs = useMemo(() => {
    if (!searchQuery.trim()) {
      return auditLogs;
    }
    
    const searchLower = searchQuery.toLowerCase();
    return auditLogs.filter(log => {
      return (
        log.event_type.toLowerCase().includes(searchLower) ||
        (log.user_email && log.user_email.toLowerCase().includes(searchLower)) ||
        (log.user_name && log.user_name.toLowerCase().includes(searchLower)) ||
        (log.user_id && log.user_id.toLowerCase().includes(searchLower)) ||
        (log.ip_address && log.ip_address.toLowerCase().includes(searchLower))
      );
    });
  }, [auditLogs, searchQuery]);

  // Sort the whole filtered list before cutting out a page. The table sorts only
  // the rows it is handed, so sorting after the slice reordered one page and left
  // the newest or oldest entries on whichever page they happened to fall.
  const sortedAuditLogs = useMemo(() => {
    const [sort] = sorting;
    if (!sort || !SORT_VALUES.has(sort.id)) return filteredAuditLogs;
    return [...filteredAuditLogs].sort((a, b) => {
      const cmp = compareLogs(a, b, sort.id);
      return sort.desc ? -cmp : cmp;
    });
  }, [filteredAuditLogs, sorting]);

  // A stale link can point past the end; the helper shows the last real page
  // and, once the logs have loaded, corrects the URL.
  const { rows: paginatedAuditLogs, currentPage } = useClientPage(
    { page, limit: pageSize, clampPage },
    sortedAuditLogs,
    !isLoading && !hasError,
  );

  const handleClearFilters = useCallback(() => {
    handleSearchChange('');
  }, [handleSearchChange]);

  const getActiveFilters = useMemo((): ActiveFilter[] => {
    const activeFilters: ActiveFilter[] = [];
    
    if (searchQuery) {
      activeFilters.push({
        key: 'search',
        label: `"${searchQuery}"`,
        value: searchQuery,
        onRemove: () => handleSearchChange('')
      });
    }

    return activeFilters;
  }, [searchQuery, handleSearchChange]);

  const globalFilterFn = useCallback((log: AuditLog, searchQuery: string) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      log.event_type.toLowerCase().includes(searchLower) ||
      (log.user_email?.toLowerCase().includes(searchLower) ?? false) ||
      (log.user_name?.toLowerCase().includes(searchLower) ?? false) ||
      (log.user_id?.toLowerCase().includes(searchLower) ?? false) ||
      (log.ip_address?.toLowerCase().includes(searchLower) ?? false)
    );
  }, []);

  const auditLogColumns: DataTableColumn<AuditLog>[] = [
    {
      id: 'status',
      header: '',
      accessorKey: 'event_type',
      cell: (log) => (
        <span
          className={`w-3 h-3 rounded-full inline-block ${
            log.event_type.includes('success')
              ? 'bg-green-500'
              : log.event_type.includes('failed')
              ? 'bg-red-500'
              : 'bg-yellow-500'
          }`}
        />
      ),
      enableSorting: false,
      size: 40,
    },
    {
      id: 'event_type',
      header: t('sso.eventType'),
      accessorKey: 'event_type',
      cell: (log) => <strong className="text-sm">{log.event_type}</strong>,
      enableSorting: true,
      sortingFn: (a, b) => compareLogs(a.original, b.original, 'event_type'),
      size: 200,
    },
    {
      id: 'user',
      header: t('sso.userEmail'),
      accessorFn: userLabel,
      cell: (log) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {userLabel(log) || '-'}
        </span>
      ),
      enableSorting: true,
      sortingFn: (a, b) => compareLogs(a.original, b.original, 'user'),
      size: 240,
      hideOnMobile: true,
    },
    {
      id: 'ip_address',
      header: t('sso.ipAddress'),
      accessorKey: 'ip_address',
      cell: (log) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{log.ip_address || '-'}</span>
      ),
      enableSorting: false,
      size: 150,
      hideOnMobile: true,
    },
    {
      id: 'created_at',
      header: t('sso.timestamp'),
      accessorKey: 'created_at',
      cell: (log) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDateTimeWithSeconds(log.created_at, timezone)}
        </span>
      ),
      enableSorting: true,
      sortingFn: (a, b) => compareLogs(a.original, b.original, 'created_at'),
      size: 180,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="w-full max-w-sm">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('sso.searchAuditLogs')}
              onClear={() => handleSearchChange('')}
              showClearButton={true}
              size="md"
            />
          </div>

          <div className="flex justify-end sm:justify-start">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAuditLogs.length}
              itemsPerPage={pageSize}
              onPageChange={onPageChange}
            />
          </div>
        </div>

        {getActiveFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('sso.activeFilters')}:</span>
            {getActiveFilters.map((filter) => (
              <div key={filter.key} className="flex items-center gap-1 px-2 py-1 bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-xs rounded-full">
                <span>{filter.label}</span>
                <button
                  onClick={filter.onRemove}
                  className="ml-1 hover:bg-primary/25 dark:hover:bg-primary/35 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-2 w-2" />
                </button>
              </div>
            ))}
            <button
              onClick={handleClearFilters}
              className="ml-auto text-xs h-6 px-2 text-primary hover:text-[hsl(var(--primary-hover))] hover:bg-primary/10 dark:hover:bg-primary/15 rounded"
            >
              {t('sso.clearAll')}
            </button>
          </div>
        )}
      </div>

      {/* Audit Logs Table */}
      {hasError ? (
        <ErrorState
          title={t('sso.failedToLoadAuditLogs')}
          description={t('sso.couldNotLoadAuditLogs')}
          onRetry={onRetry}
        />
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            data={paginatedAuditLogs}
            columns={auditLogColumns}
            searchQuery={searchQuery}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage={t('sso.noAuditLogs')}
            emptyIcon={History}
            globalFilterFn={globalFilterFn}
            enablePagination={false}
            isLoading={isLoading && auditLogs.length === 0}
          />
        </div>
      )}
    </div>
  );
}
